/**
 * Wrapper minimalista sobre fetch para hablar con el API.
 *
 * - `credentials: 'include'` siempre (cookie HttpOnly)
 * - Inyecta `Content-Type` cuando el body es un objeto
 * - Lanza `ApiClientError` con shape consistente { code, message, status, details }
 * - 401 NO redirige automáticamente: el caller decide (LoginPage no debe).
 */

import { DEMO, resolveDemo } from '../demo/index.js';
import { esSesionQueSeCerro } from '../lib/sesionCerrada.js';
import { productoActivo } from '../lib/productoActivo.js';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * QUÉ DECIR CUANDO LA RESPUESTA NO TRAE MENSAJE.
 *
 * El motor contesta sus errores en JSON con `code` y `message`, y eso es lo que se muestra.
 * Pero hay respuestas que no vienen de él: el servidor de pantallas que no logra hablarle
 * mientras se está reemplazando, un tiempo de espera agotado, una respuesta vacía. Ahí `body`
 * no es un objeto y hasta hoy se mostraba **"Error desconocido"**, que no le dice nada a nadie
 * y encima suena a que algo se rompió cuando lo normal es que el motor esté reiniciándose.
 *
 * Ninguno de estos textos nombra un componente ni un código: dicen qué pasó y qué hacer.
 */
function mensajeSinCuerpo(status: number): string {
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return 'No se pudo hablar con el servidor. Puede estar actualizándose; vuelve a intentar en unos segundos.';
  }
  if (status === 408) return 'La respuesta tardó demasiado. Vuelve a intentar.';
  if (status === 413) return 'El archivo es demasiado grande.';
  if (status === 429) return 'Demasiadas solicitudes seguidas. Espera un momento y vuelve a intentar.';
  if (status >= 500) return 'El servidor tuvo un problema al responder. Vuelve a intentar en unos segundos.';
  if (status === 404) return 'No se encontró lo que se pidió.';
  if (status >= 400) return 'No se pudo completar la operación.';
  return 'No se pudo completar la operación.';
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly body: unknown;

  constructor(status: number, body: ApiErrorBody | unknown) {
    const delMotor = body && typeof body === 'object' && typeof (body as ApiErrorBody).message === 'string'
      ? (body as ApiErrorBody)
      : undefined;
    const b = delMotor ?? { code: 'SIN_RESPUESTA', message: mensajeSinCuerpo(status) };
    super(b.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = b.code ?? 'UNKNOWN';
    this.details = b.details;
    this.body = body;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Si true, NO lanza para códigos 3xx (útil para login multi-tenant 300). */
  acceptStatus?: number[];
}

const BASE = ''; // usamos el proxy de Vite (/api)

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, acceptStatus, headers, ...rest } = opts;

  // MODO DEMO: sin backend, resolvemos con fixtures según la etapa del
  // recorrido. El body viaja al resolver: los POST del visitante (crear
  // proyecto, elegir catálogos, generar) avanzan la etapa.
  if (DEMO) {
    await new Promise((r) => setTimeout(r, 80)); // micro-delay para que se vean los loaders
    return resolveDemo(path, (rest.method ?? 'GET'), body) as T;
  }

  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      // Producto activo (ai/ci) según el hostname. La API no ve el subdominio tras
      // el proxy de Vite, así que se lo declaramos aquí (ver lib/productoActivo).
      'X-Opus-App': productoActivo(),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  // CUANDO NO HAY NADIE DEL OTRO LADO. Si el motor está caído o reiniciándose, `fetch` no
  // devuelve una respuesta: TIRA. Sin esto, lo que llega a la pantalla es el "Failed to fetch"
  // del navegador —en inglés y hablando de otra cosa— o un "Error desconocido". Se convierte en
  // el mismo error que el resto, con estado 0, para que cada pantalla lo trate igual.
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch {
    throw new ApiClientError(0, undefined);
  }

  let parsed: unknown = undefined;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  const ok = res.ok || (acceptStatus?.includes(res.status) ?? false);
  if (!ok) {
    const body = parsed as ApiErrorBody | undefined;
    // OPUS_SUBSCRIPTION_INACTIVE: cross-license m1↔m0 (B.1 review OPUS Total
    // 2026-05-15). Hard requirement: sin suscripción OPUS 27 activa, no se
    // accede a OPUS ai. Disparamos evento global para que el gate visual
    // (Dialog modal) lo muestre sin que cada caller tenga que manejarlo.
    // En /login LoginPage muestra el hard-block (como el hub). Fuera de
    // login, el gate global abre el diálogo Renovar / Cerrar.
    if (
      res.status === 403 &&
      body?.code === 'OPUS_SUBSCRIPTION_INACTIVE' &&
      !(typeof window !== 'undefined' && window.location.pathname.startsWith('/login'))
    ) {
      window.dispatchEvent(new CustomEvent('opus:subscription-inactive', { detail: body }));
    }
    // OPUS_EN_MANTENIMIENTO: el motor no está contestando porque lo estamos
    // reemplazando (despliegue en curso). NO lo contesta el motor —no puede, no
    // está—: lo contesta el nginx de esta pantalla cuando no logra hablarle (ver
    // infra/nginx/nginx.conf → @motor_ausente). Se avisa por evento global para
    // que `MantenimientoGate` ponga la pantalla de "estamos actualizando" encima
    // de todo, y la retire sola cuando el motor vuelva. Cada llamador no tiene
    // que saber nada de esto: le llega su error como siempre.
    if (res.status === 503 && body?.code === 'OPUS_EN_MANTENIMIENTO') {
      window.dispatchEvent(new CustomEvent('opus:en-mantenimiento', { detail: body }));
    }
    // UNAUTHENTICATED / INVALID_TOKEN: la sesión se cerró mientras la persona
    // trabajaba. Se avisa por evento global para que `SesionCerradaGate` ponga el
    // aviso encima de todo; sin eso, el texto crudo del motor —"Sesión requerida"—
    // se renderizaba donde cayera y la pantalla quedaba a medias. `INVALID_CREDENTIALS`
    // NO entra acá: ese es el 401 de una contraseña equivocada y lo muestra el
    // propio formulario de ingreso.
    if (esSesionQueSeCerro(res.status, body?.code)) {
      window.dispatchEvent(new CustomEvent('opus:sesion-cerrada', { detail: body }));
    }
    // QUOTA_EXCEEDED: el tenant alcanzó la cuota mensual de algún counter
    // (proyectos / chats / conceptos). Hard cap provisional C.5. El toast
    // global lo informa al usuario y refresca el medidor.
    if (res.status === 403 && body?.code === 'QUOTA_EXCEEDED') {
      window.dispatchEvent(new CustomEvent('opus:quota-exceeded', { detail: body }));
    }
    throw new ApiClientError(res.status, parsed);
  }
  return parsed as T;
}
